import { parsePrFailure, type PrCreationStep } from '@pr-app/shared';

/** D365's own wording when the service user has no worker to take the Preparer from. */
export const PREPARER_REFUSAL = /Field 'Preparer' must be filled in/i;
/** D365's own wording when the dimension value has a different number of segments than its active format. */
export const DIMENSION_FORMAT_REFUSAL =
  /number of dimension values specified .* is different from the number set up in the active format/i;

/** What a person is told to do about the refusals the app recognises, in plain words. */
export function plainFailureHints(message: string, again: string): string[] {
  const hints: string[] = [];
  if (DIMENSION_FORMAT_REFUSAL.test(message)) {
    hints.push(
      "D365's financial dimension format does not match what the app sends. Ask your D365 " +
        'administrator for the active format used for data entities (General ledger → Chart of ' +
        'accounts → Dimensions) and the position of the cost centre in it, then set ' +
        `D365_DIMENSION_FORMAT to match and ${again}.`,
    );
  }
  if (PREPARER_REFUSAL.test(message)) {
    hints.push(
      "D365 needs a Preparer on every requisition. Ask your D365 administrator to link the app's " +
        'D365 service user to a worker (System administration → Users → User relations), then ' +
        `${again}.`,
    );
  }
  return hints;
}

interface AuditLike {
  action: string;
  comment: string | null;
  createdAt: string;
}

/**
 * The failed D365 attempt a request still in review is waiting on, or null. A failure only counts
 * while it is newer than the requester's last (re)submission: a request they fixed and sent in
 * again starts clean.
 */
export function pendingCreationFailure(
  status: string,
  auditLog: readonly AuditLike[],
): { message: string; at: string; step: PrCreationStep | null } | null {
  if (status !== 'PENDING_REVIEW') return null;
  const newestFirst = auditLog.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt));
  for (const entry of newestFirst) {
    if (entry.action === 'SUBMITTED' || entry.action === 'RESUBMITTED') return null;
    if (entry.action === 'PR_FAILED') {
      const parsed = parsePrFailure(entry.comment ?? '');
      return { message: parsed.message, at: entry.createdAt, step: parsed.step };
    }
  }
  return null;
}

/**
 * A failure at the LINES step means the requisition header (and perhaps some lines) already
 * exists in D365. Approving again writes only what is missing, so a change to something already
 * written does not reach it. Said plainly, because "edit and approve again" otherwise promises
 * more than it does.
 */
export function partialWriteHint(step: PrCreationStep | null): string | null {
  if (step !== 'LINES') return null;
  return (
    'Part of this request was already written to D365. Changes you make now apply to anything not ' +
    'yet written; if you change something that was already written (the project, or a line that ' +
    'went in), ask your D365 administrator to delete that draft requisition first so the next ' +
    'approval writes it fresh.'
  );
}
